import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from './useAppDispatcher.hook';
import { useAppSelector } from './useAppSelector.hook';
import { getUserId } from '../redux/User/UserSlice';
import { syncAttachmentsThunk } from '../redux/Attachment/Attachment.thunk';
import { getCandidateInfoThunk } from '../redux/Candidate/Candidate.thunk';
import { PersonRecord } from '../redux/Candidate/Record/Record.type';
import { CalendarCv } from '../redux/Calendar/Calendar.mapper';
import { PERSON_ATTACH_OBJECT } from '../constants/attachment.constant';
import { uuidv4 } from '../utils/uuid';

export const useCalendarCvUpload = ({
  record,
  isSubmitting,
  onUploaded,
}: {
  record: PersonRecord;
  isSubmitting: boolean;
  onUploaded: (uuid: string, fileName: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(getUserId);
  const { t } = useTranslation();
  const [isUploadingCv, setIsUploadingCv] = useState(false);
  const [cvUploadError, setCvUploadError] = useState<string | null>(null);
  const [savedCvs, setSavedCvs] = useState<CalendarCv[]>([]);
  const cvUploadInFlight = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    generation.current++;
    setSavedCvs([]);
    setCvUploadError(null);
    setIsUploadingCv(false);
    cvUploadInFlight.current = false;
    return () => {
      generation.current++;
    };
  }, [record.id, record.personId]);

  const handleUploadCv = useCallback(
    async (file: File) => {
      if (cvUploadInFlight.current || isSubmitting) return;
      cvUploadInFlight.current = true;
      setIsUploadingCv(true);
      setCvUploadError(null);
      const currentGeneration = generation.current;
      const isCurrent = () => currentGeneration === generation.current;
      const uuid = uuidv4();

      try {
        const result = await dispatch(
          syncAttachmentsThunk(
            { type: PERSON_ATTACH_OBJECT, id: record.personId },
            [
              {
                tmpUUID: uuid,
                author_id: userId ?? 0,
                type: 'RESUME',
                file,
                is_private: 0,
              },
            ],
          ),
        );
        if (!result) throw new Error('CV upload failed');
      } catch (error) {
        if (isCurrent()) setCvUploadError(t('calendarEvent.uploadError'));
        throw error;
      } finally {
        if (isCurrent()) {
          cvUploadInFlight.current = false;
          setIsUploadingCv(false);
        }
      }

      if (!isCurrent()) return;

      setSavedCvs(prev => [
        ...prev,
        {
          uuid,
          type: 'RESUME',
          isCloud: true,
          isPrivate: false,
          url: file.name,
          createdAt: Date.now(),
        },
      ]);
      onUploaded(uuid, file.name);

      void dispatch(getCandidateInfoThunk(record.personId));
    },
    [
      dispatch,
      isSubmitting,
      record.personId,
      t,
      userId,
      onUploaded,
    ],
  );

  return { handleUploadCv, isUploadingCv, cvUploadError, savedCvs };
};
